import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
  useTheme
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../contexts/AuthContext';

interface UserMenuProps {
  variant?: 'desktop' | 'mobile';
  onClose?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ variant = 'desktop', onClose }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const muiTheme = useTheme();
  
  // Define theme-specific styles
  const iconColor = 'secondary.light';
  const iconHoverColor = 'secondary.main';
  
  // Menu paper styles
  const menuPaperStyles = {
    backgroundColor: muiTheme.palette.background.paper,
    border: '1px solid',
    borderColor: 'secondary.light',
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseMenu();
    if (onClose) onClose();
  };

  const handleMenuItemClick = () => {
    handleCloseMenu();
    if (onClose) onClose();
  };

  // For mobile view, we just return the menu items directly
  if (variant === 'mobile') {
    return (
      <>
        {isAuthenticated ? (
          <>
            <MenuItem 
              onClick={handleMenuItemClick}
              component={RouterLink}
              to="/profile"
            >
              <Typography textAlign="center">My Account</Typography>
            </MenuItem>
            <MenuItem 
              onClick={handleLogout}
            >
              <Typography textAlign="center">Logout</Typography>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem 
              onClick={handleMenuItemClick}
              component={RouterLink}
              to="/login"
            >
              <Typography textAlign="center">Sign In</Typography>
            </MenuItem>
            <MenuItem 
              onClick={handleMenuItemClick}
              component={RouterLink}
              to="/signup"
            >
              <Typography textAlign="center">Create Account</Typography>
            </MenuItem>
          </>
        )}
        
        {isAuthenticated && user && user.is_superuser && (
          <MenuItem 
            onClick={handleMenuItemClick}
            component={RouterLink}
            to="/admin"
          >
            <Typography textAlign="center">Admin Dashboard</Typography>
          </MenuItem>
        )}
      </>
    );
  }

  // For desktop view, we return the icons with dropdown menu
  return (
    <>
      {isAuthenticated ? (
        <>
          <Tooltip title="Account">
            <IconButton 
              onClick={handleOpenMenu} 
              aria-label="account"
              sx={{ 
                ml: 1,
                color: iconColor,
                '&:hover': {
                  color: iconHoverColor,
                }
              }}
            >
              {user?.full_name ? (
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user.full_name.charAt(0).toUpperCase()}
                </Avatar>
              ) : user?.email ? (
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user.email.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircleIcon />
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            sx={{ mt: '45px' }}
            id="menu-user"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              sx: menuPaperStyles
            }}
          >
            {user && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.dark' }}>
                  {user.full_name || user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ bgcolor: 'secondary.light', opacity: 0.3 }} />
            
            <MenuItem 
              onClick={handleMenuItemClick}
              component={RouterLink}
              to="/profile"
            >
              <Typography textAlign="center">My Account</Typography>
            </MenuItem>
            
            <MenuItem 
              onClick={handleLogout}
            >
              <Typography textAlign="center">Logout</Typography>
            </MenuItem>
            
            {isAuthenticated && user && user.is_superuser && (
              <>
                <Divider sx={{ bgcolor: 'secondary.light', opacity: 0.3 }} />
                <MenuItem 
                  onClick={handleMenuItemClick}
                  component={RouterLink}
                  to="/admin"
                >
                  <Typography textAlign="center">Admin Dashboard</Typography>
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      ) : (
        <>
          <Tooltip title="Sign In">
            <IconButton
              component={RouterLink}
              to="/login"
              aria-label="sign in"
              sx={{ 
                ml: 1,
                color: iconColor,
                '&:hover': {
                  color: iconHoverColor,
                }
              }}
            >
              <PersonIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Create Account">
            <IconButton
              component={RouterLink}
              to="/signup"
              aria-label="create account"
              sx={{ 
                ml: 1,
                color: iconColor,
                '&:hover': {
                  color: iconHoverColor,
                }
              }}
            >
              <PersonAddIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
      
      {isAuthenticated && user && user.is_superuser && (
        <Tooltip title="Admin Dashboard">
          <IconButton
            component={RouterLink}
            to="/admin"
            aria-label="admin panel"
            sx={{ 
              ml: 1,
              color: iconColor,
              '&:hover': {
                color: iconHoverColor,
              }
            }}
          >
            <AdminPanelSettingsIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default UserMenu; 